import _ from 'lodash';
import { ActiveFeedIcon, InactiveFeedIcon, NeedsAttentionFeedIcon, ProcessingFeedIcon } from '../icons';


export const SIDE_BAR_WIDTH = "240px";
export const MAIN_CONTENT_MIN_WIDTH = "1160px";
export const MAIN_CONTENT_MIN_HEIGHT = "700px";
export const TOP_NAV_HEIGHT = "80px";

export const ACTIVE = "active";
export const NEEDS_ATTENTION = "needsAttention";
export const PROCESSING = "processing";
export const INACTIVE = "inactive";


export const FEED_STATUSES = [ACTIVE, NEEDS_ATTENTION, PROCESSING, INACTIVE]
export const FEED_STATUSES_LIST = [...FEED_STATUSES] as const
export type FEED_STATUSES_TYPE = typeof FEED_STATUSES[number]

export type SelectOptionType<T = string> = {
  label: string
  value: T
}

// Keys should match with values inside STATUSES constant above
export const FEED_STATUSES_AS_TEXT = {
  active: "ACTIVE",
  needsAttention: "NEED ATTENTION",
  processing: "PROCESSING",
  inactive: "INACTIVE",
};

// Keys should match with values inside STATUSES constant above
export const FEED_STATUSES_AS_COLOR = {
  active: "#6267e6",
  needsAttention: "#f94d63",
  processing: "#fac766",
  inactive: "#babac8",
};

// Keys should match with values inside STATUSES constant above
export const selectIconByStatus = (status: FEED_STATUSES_TYPE) => {
  if (status === "needsAttention") {
    return <NeedsAttentionFeedIcon />;
  }

  if (status === "processing") {
    return <ProcessingFeedIcon />;
  }

  if (status === "inactive") {
    return <InactiveFeedIcon />;
  }

  return <ActiveFeedIcon />;
};

// Keys should match with values inside STATUSES constant above
export const FEED_STATUSES_AS_DESCRIPTIONS = {
  active: "All file metrics are currently healthy",
  needsAttention: "A potential issue with some file metrics were identified",
  processing: "The current feed is processing",
  inactive: "This feed is currently inactive",
};

// Keys should match with values inside STATUSES constant above
export const FEED_STATUSES_AS_LABELS = {
  active: "Active",
  needsAttention: "Needs Attention",
  processing: "Processing",
  inactive: "Inactive",
};

export const SPOT_EXPOSURE_CRITERIA = [
  "smba_id",
  "network",
  "network_id",
  "advertiser",
  "advertiser_id",
  "brand",
  "brand_id",
  "tv_spot_id",
  "tv_spot_name",
  "schedule_ts",
  "exposure_ts",
  "daypart",
  "buy_type",
  "prior_title",
  "prior_title_id",
  "next_title",
  "next_title_id",
  "product",
  "product_id",
  "duration",
  "feed_id",
  "creative_url",
  "intermediate_id",
];

export enum GROUPED_VALUES {
  DEVICE_INPUT_SE = 'device_input_se',
  DEVICE_INPUT_CV = 'device_input_cv'
}

export const US_SPOT_EXPOSURE = ["dma", "zip"];

export const INT_SPOT_EXPOSURE = ["subdivision", "city", "postal_code"];

export const SPOT_EXPOSURE_MUST_COLUMN = {
  smba_id: true,
  network: true,
  network_id: true,
  advertiser: true,
  advertiser_id: true,
  brand: true,
  brand_id: true,
  tv_spot_id: true,
  tv_spot_name: true,
};

export const ALL_CONTENT_CRITERIA = _.uniq(['smba_id', 'intermediate_id', 'exposure_start_ts', 'exposure_end_ts', 'exposure_duration', 'content_type', 'content_id', 'title', 'episode_title', 'season', 'episode', 'description', 'genres', 'release_date', 'network', 'network_id', 'affiliate_call_sign', 'scheduled_program_start_ts', 'scheduled_program_end_ts', 'channel_content_offset_s', 'program_content_offset_s', 'application', 'household_weight', 'tv_input_type', 'source_device', 'source_device_category', 'application_availability', 'app_id', 'source_device_id', 'source_device_os', 'feed_id'])
export const ALL_AD_CRITERIA = _.uniq(['smba_id', 'intermediate_id', 'schedule_ts', 'exposure_start_ts', 'daypart', 'network', 'network_id', 'prior_title', 'prior_title_id', 'next_title', 'next_title_id', 'advertiser', 'advertiser_id', 'brand', 'brand_id', 'product', 'product_id', 'tv_spot_name', 'tv_spot_id', 'total_duration', 'buy_type', 'creative_url', 'audience_scope', 'ad_source', 'content_type', 'application', 'application_availability', 'app_id', 'feed_id'])
export const ALL_CRITERIA = _.uniq([...ALL_CONTENT_CRITERIA, ...ALL_AD_CRITERIA])

export const X_REF_US_CRITERIA = [
  "smba_id",
  "hh_id",
  "ip_address",
  "ip_first_seen",
  "ip_last_seen",
  "zip_code",
  "state",
];

export const X_REF_NON_US_CRITERIA = [
  "smba_id",
  "hh_id",
  "ip_address",
  "ip_first_seen",
  "ip_last_seen",
  "postal_code",
  "subdivision",
];
export const TIVO_X_REF_CRITERIA = [
  "smba_id",
  "hh_id",
  "ip_address",
  "ip_last_seen",
  "zip_code",
];

export const GEO_WEIGHT_CRITERIA = ["smba_id", "hh_id", "geo_weight"];

export const VIEWERSHIP_CRITERIA = [
  "smba_id",
  "network",
  "affiliate_call_sign",
  "ssid",
  "content_id",
  "title",
  "viewership_offset_s",
  "content_recognition_start_ts",
  "content_recognition_end_ts",
  "content_start_ts",
  "content_end_ts",
  "interval_start_ts",
  "interval_end_ts",
  "geo_weight",
  "dma",
  "zip",
  "feed_id",
];

export const STREAMING_GEO_PANEL_CRITERIA = [
  'smba_id',
  'household_id',
  'household_weight',
]

export type FEED_TYPE = 'viewership' | 'unexposed' | 'spot exposure' | 'tivo spot exposure' | 'geo weights' |
  'active' | 'metadata' | 'content viewership' | 'content viewership commercial' | 'tivo content viewership' |
  'demographic panel' | 'x_ref' | 'tivo x_ref' | 'tivo+acr x_ref' |
  'all viewership' | 'all viewership ad' | 'all viewership content' |
  'tivo all viewership' | 'tivo all viewership ad' | 'tivo all viewership content' |
  'streaming geo panel'

export const FEED_TYPE_MAPPING: Record<FEED_TYPE, String> = {
  'viewership': "Viewership",
  'unexposed': "Unexposed",
  "spot exposure": "TV Ad Exp.",
  "tivo spot exposure": "Tivo Ad Exp.",
  "geo weights": "Geo Weights",
  'active': "Active",
  'metadata': "Metadata",
  "content viewership": "Content Viewership",
  "content viewership commercial": "Content Viewership Commercial",
  "tivo content viewership": "Tivo Content Viewership",
  "demographic panel": "Demographic Panel",
  x_ref: "X-Ref",
  "tivo x_ref": "Tivo X-Ref",
  "tivo+acr x_ref": "Tivo+Acr X-Ref",
  "all viewership": "All Viewership",
  "all viewership ad": "All Viewership Ad",
  "all viewership content": "All Viewership Content",
  "tivo all viewership": "Tivo All Viewership",
  "tivo all viewership ad": "Tivo All Viewership Ad",
  "tivo all viewership content": "Tivo All Viewership Content",
  'streaming geo panel': "Streaming Geo Panel",
};

export const getFeedTypeOption = (key: FEED_TYPE) => ({
  label: FEED_TYPE_MAPPING[key],
  value: key,
})

const BRAND_FILTERING_UNSUPPORTED_TYPES: FEED_TYPE[] = [
  'viewership',
  'content viewership',
  'content viewership commercial',
]

export const supportsBrandFiltering = (type: string): boolean =>
  !BRAND_FILTERING_UNSUPPORTED_TYPES.includes(type as FEED_TYPE)

// Types that require kantar_filter_expression and quality_metrics to be cleared on submit.
// Includes x_ref types which skip the IncludedContent UI step entirely but still need cleanup.
const SUBMIT_CLEAR_FILTER_TYPES: FEED_TYPE[] = [
  'viewership',
  'content viewership',
  'content viewership commercial',
  'x_ref',
  'tivo x_ref',
  'tivo+acr x_ref',
]

export const requiresFilterExpressionClearOnSubmit = (type: string): boolean =>
  SUBMIT_CLEAR_FILTER_TYPES.includes(type as FEED_TYPE)

export const TAB_VIEW_HEADER_ORDER: FEED_TYPE[] = [
  "spot exposure",
  "tivo spot exposure",
  "unexposed",
  "viewership",
  "active",
  "metadata",
  "content viewership",
  "content viewership commercial",
  "tivo content viewership",
  "geo weights",
  "demographic panel",
  "x_ref",
  "tivo x_ref",
  "tivo+acr x_ref",
  "all viewership",
  "all viewership ad",
  "all viewership content",
  "tivo all viewership",
  "tivo all viewership ad",
  "tivo all viewership content",
  "streaming geo panel",
];

export const CONTENT_VIEWERSHIP_CRITERIA = [
  "smba_id",
  "exposure_start_ts",
  "exposure_end_ts",
  "duration",
  "content_type",
  "content_id",
  "title",
  "episode_title",
  "season",
  "episode",
  "description",
  "genres",
  "release_date",
  "network",
  "network_id",
  "affiliate_call_sign",
  "scheduled_program_start_ts",
  "scheduled_program_end_ts",
  "channel_content_offset_s",
  "program_content_offset_s",
  "application",
  "intermediate_id",
];


export const ALL_VIEWERSHIP_CRITERIA = [
  "smba_id",
  "schedule_ts",
  "exposure_start_ts",
  "exposure_end_ts",
  "scheduled_program_start_ts",
  "scheduled_program_end_ts",
  "total_duration",
  "daypart",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "prior_title",
  "prior_title_id",
  "next_title",
  "next_title_id",
  "parent",
  "parent_id",
  "advertiser",
  "advertiser_id",
  "brand",
  "brand_id",
  "product",
  "product_id",
  "tv_spot_name",
  "tv_spot_id",
  "exposure_duration",
  "buy_type",
  "creative_url",
  "content_type",
  "application",
  "app_id",
  "application_availability",
  "app_probability_score",
  "audience_scope",
  "ad_source",
  "tv_input_type",
  "source_device",
  "source_device_category",
  "syndicated_by",
  "content_id",
  "original_content_id",
  "title",
  "episode_title",
  "season",
  "episode",
  "description",
  "genres",
  "release_date",
  "channel_content_offset_s",
  "program_content_offset_s",
  "source_device_id",
  "source_device_os",
  "mvpd",
  "mvpd_id",
  "is_live",
  "content_format",
  "title_id",
  "viewership_type",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
  "intermediate_id"
];

export const ALL_VIEWERSHIP_AD_CRITERIA = [
  "smba_id",
  "schedule_ts",
  "exposure_start_ts",
  "exposure_end_ts",
  "total_duration",
  "exposure_duration",
  "daypart",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "prior_title",
  "prior_title_id",
  "next_title",
  "next_title_id",
  "parent",
  "parent_id",
  "advertiser",
  "advertiser_id",
  "brand",
  "brand_id",
  "product",
  "product_id",
  "tv_spot_name",
  "tv_spot_id",
  "buy_type",
  "creative_url",
  "content_type",
  "application",
  "app_id",
  "application_availability",
  "app_probability_score",
  "audience_scope",
  "ad_source",
  "tv_input_type",
  "source_device",
  "source_device_category",
  "source_device_id",
  "source_device_os",
  "mvpd",
  "mvpd_id",
  "syndicated_by",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
  "intermediate_id"
];

export const ALL_VIEWERSHIP_CONTENT_CRITERIA = [
  "smba_id",
  "exposure_start_ts",
  "exposure_end_ts",
  "exposure_duration",
  "scheduled_program_start_ts",
  "scheduled_program_end_ts",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "content_type",
  "application",
  "app_id",
  "application_availability",
  "app_probability_score",
  "tv_input_type",
  "source_device",
  "source_device_category",
  "syndicated_by",
  "total_duration",
  "content_id",
  "original_content_id",
  "title",
  "episode_title",
  "season",
  "episode",
  "description",
  "genres",
  "release_date",
  "channel_content_offset_s",
  "program_content_offset_s",
  "source_device_id",
  "source_device_os",
  "mvpd",
  "mvpd_id",
  "is_live",
  "content_format",
  "title_id",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
  "intermediate_id"
];

export const TIVO_ALL_VIEWERSHIP_CRITERIA = [
  "smba_id",
  "schedule_ts",
  "exposure_start_ts",
  "exposure_end_ts",
  "scheduled_program_start_ts",
  "scheduled_program_end_ts",
  "total_duration",
  "daypart",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "prior_title",
  "prior_title_id",
  "next_title",
  "next_title_id",
  "parent",
  "parent_id",
  "advertiser",
  "advertiser_id",
  "brand",
  "brand_id",
  "product",
  "product_id",
  "tv_spot_name",
  "tv_spot_id",
  "exposure_duration",
  "buy_type",
  "creative_url",
  "content_type",
  "audience_scope",
  "ad_source",
  "syndicated_by",
  "content_id",
  "original_content_id",
  "title",
  "episode_title",
  "season",
  "episode",
  "description",
  "genres",
  "release_date",
  "channel_content_offset_s",
  "program_content_offset_s",
  "mvpd",
  "mvpd_id",
  "is_live",
  "content_format",
  "title_id",
  "viewership_type",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
];

export const TIVO_ALL_VIEWERSHIP_AD_CRITERIA = [
  "smba_id",
  "schedule_ts",
  "exposure_start_ts",
  "exposure_end_ts",
  "total_duration",
  "exposure_duration",
  "daypart",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "prior_title",
  "prior_title_id",
  "next_title",
  "next_title_id",
  "parent",
  "parent_id",
  "advertiser",
  "advertiser_id",
  "brand",
  "brand_id",
  "product",
  "product_id",
  "tv_spot_name",
  "tv_spot_id",
  "buy_type",
  "creative_url",
  "content_type",
  "audience_scope",
  "ad_source",
  "mvpd",
  "mvpd_id",
  "syndicated_by",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
];

export const TIVO_ALL_VIEWERSHIP_CONTENT_CRITERIA = [
  "smba_id",
  "exposure_start_ts",
  "exposure_end_ts",
  "exposure_duration",
  "scheduled_program_start_ts",
  "scheduled_program_end_ts",
  "network",
  "network_title",
  "network_id",
  "affiliate_call_sign",
  "channel_id",
  "content_type",
  "total_duration",
  "content_id",
  "original_content_id",
  "title",
  "episode_title",
  "season",
  "episode",
  "description",
  "genres",
  "release_date",
  "channel_content_offset_s",
  "program_content_offset_s",
  "mvpd",
  "mvpd_id",
  "is_live",
  "content_format",
  "title_id",
  "region",
  "region_name",
  "postal_code",
  "subdivision",
  "city",
  "feed_id",
];


export const DEMOGRAPHIC_PANEL_CRITERIA = [
  "smba_id",
  "hh_id",
  "hh_demo_weight",
];

export const minStarDateForGeoWeights = "2022-01-01";
export const minStartDateForXref = "2022-01-01";
export const minStartDateForTivoXref = "2022-12-04";

export type availableForContentType = 'spot exposure' | 'content viewership' | 'all viewership' | 'all viewership ad' | 'all viewership content'

export const buyOrContentTypeFeeds: Record<availableForContentType, string> = {
  "spot exposure": "Buy Type",
  "content viewership": "Content Type",
  "all viewership": "Content Type",
  "all viewership ad": "Content Type",
  "all viewership content": "Content Type",
};

export const buyOrContentTypeFeedsParam: Record<availableForContentType, string> = {
  "spot exposure": "buy_type",
  "content viewership": "content_type",
  "all viewership": "content_type",
  "all viewership ad": "content_type",
  "all viewership content": "content_type",
};


const contentType = {
  deprecated: [
    { label: "Linear + OTT", value: "both" },
    { label: "Linear", value: "linear" },
    { label: "OTT", value: "ott" },
  ],
  allViewership: [
    { label: "Streaming", value: "streaming" },
    { label: "Linear TV", value: "linear tv" },
    { label: "Linear TV + Streaming", value: "both" },
  ]
}

export const buyOrContentTypeFeedsOptions: Record<availableForContentType, SelectOptionType[]> = {
  "spot exposure": contentType.deprecated,
  "content viewership": contentType.deprecated,
  "all viewership": contentType.allViewership,
  "all viewership ad": contentType.allViewership,
  "all viewership content": contentType.allViewership,
}

export const buyOrContentTypeMapping = {
  'both': "Linear + OTT",
  'linear': "Linear",
  'ott': "OTT",
  'linear tv': 'Linear TV',
  'streaming': 'Streaming',
};

export const xRefCadenceOptions = [
  { value: "mon", label: "Monday" },
  { value: "tue", label: "Tuesday" },
  { value: "wed", label: "Wednesday" },
  { value: "thu", label: "Thursday" },
  { value: "fri", label: "Friday" },
  { value: "sat", label: "Saturday" },
  { value: "sun", label: "Sunday" },
];

export const ENABLE_DELIVERY_THRESHOLD_FEED_AGE = 23;


export type FEED_VERSION_TYPE = '0.1.0' |
  '1.2.0' |
  '1.4.0' | '1.4.1' | '1.4.2' | '1.4.2' | '1.4.2' | '1.4.3' | '1.4.4' | '1.4.5' | '1.4.6' | '1.4.7' |
  '1.6.0' | '1.6.1' | '1.6.2' | '1.6.5' | '1.6.6' | '1.6.7' | '1.6.8' |
  '1.7.0' |  '2.0.0' | '2.0.1' | '2.1.0'

export const FEED_VERSIONS: { [key: string]: SelectOptionType<FEED_VERSION_TYPE> } = {
  v0_1_0: {
    value: '0.1.0', label: 'v0.1.0',
  },
  v1_2_0: {
    value: "1.2.0", label: "v1.2.0 Tivo",
  },
  // 1.4.x
  v1_4_0: {
    value: "1.4.0", label: "v1.4.0 Content Viewership",
  },
  v1_4_1: {
    value: "1.4.1", label: "v1.4.1 Content Viewership (Digital Element geo)",
  },
  v1_4_2: {
    value: "1.4.2", label: "v1.4.2 Kinetiq One Ad Impression Spot Exposure",
  },
  v1_4_2_Kantar: {
    value: "1.4.2", label: "v1.4.2 Kantar Spot Exposure",
  },
  v1_4_2_Harvester: {
    value: "1.4.2", label: "v1.4.2 Harvester One Ad Impression Spot Exposure",
  },
  v1_4_3: {
    value: "1.4.3", label: "v1.4.3 Harvester One Ad Impression BVOD Spot Exposure",
  },
  v1_4_4: {
    value: "1.4.4", label: "v1.4.4 Kinetiq Spot Exposure",
  },
  v1_4_5: {
    value: "1.4.5", label: "v1.4.5 Kinetiq Buy Type Enhanced Spot Exposure",
  },
  v1_4_6: {
    value: "1.4.6", label: "v1.4.6 Kinetiq Spot Exposure",
  },
  v1_4_7: {
    value: "1.4.7", label: "v1.4.7 Kinetiq Buy Type Enhanced Spot Exposure (Digital Element geo)",
  },
  // 1.6.x
  v1_6_0: {
    value: '1.6.0', label: 'v1.6.0 Geocorrected',
  },
  v1_6_1: {
    value: '1.6.1', label: 'v1.6.1 Geocorrected',
  },
  v1_6_2: {
    value: '1.6.2', label: 'v1.6.2 Geocorrected',
  },
  v1_6_5: {
    value: '1.6.5', label: 'v1.6.5 Geocorrected',
  },
  v1_6_6: {
    value: '1.6.6', label: 'v1.6.6 Geocorrected',
  },
  v1_6_7: {
    value: '1.6.7', label: 'v1.6.7 Geocorrected',
  },
  v1_6_8_geo_weights: {
    value: "1.6.8", label: "v1.6.8 Legacy Geo Panel",
  },
  v1_7_0: {
    value: "1.7.0", label: "v1.7.0 Streaming",
  },
  v2_0_0: {
    value: "2.0.0", label: "v2.0.0",
  },
  v2_1_0: {
    value: "2.1.0", label: "v2.1.0",
  },
  v2_0_1_geo_weights: {
    value: '2.0.1', label: "v2.0.0 Streaming Geo Panel"
  }
}


type DATA_FORMAT_OPTIONS_VALUE = 'csv' | 'parquet' | 'json'

export const DATA_FORMAT_OPTIONS: SelectOptionType<DATA_FORMAT_OPTIONS_VALUE>[] = [
  { label: "csv", value: "csv" },
  { label: "parquet", value: "parquet" },
  { label: "json", value: "json" },
];

export const DATA_FORMAT_OPTIONS_BY_VALUE = new Map(DATA_FORMAT_OPTIONS.map((option) => [option.value, option]))

export const GEO_DATA_SOURCE = [
  { label: 'Digital Element', value: 'digital_element' },
  { label: 'Maxmind', value: 'maxmind' },
]